import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Sparkles, Upload, X, Loader2, Check, AlertTriangle, RefreshCw, Save, Facebook, Instagram, Film, Square, RectangleVertical, Lightbulb, PenLine, ShieldCheck, Palette } from 'lucide-react';
import TikTokIcon from '../icons/TikTokIcon';
import DesignerPanel from './DesignerPanel';
import ReelBuilderPanel from './ReelBuilderPanel';

const GOALS = [
  { value: 'vendere', label: 'Vendere' },
  { value: 'farsi_conoscere', label: 'Farsi conoscere' },
  { value: 'fidelizzare', label: 'Fidelizzare' },
  { value: 'ricorrenza', label: 'Ricorrenza / Festività' },
];
const FORMATS = [
  { value: 'post', label: 'Post', Icon: Square },
  { value: 'story', label: 'Story', Icon: RectangleVertical },
  { value: 'reel', label: 'Reel', Icon: Film },
];
const PLATFORMS = [
  { value: 'facebook', label: 'Facebook', Icon: Facebook },
  { value: 'instagram', label: 'Instagram', Icon: Instagram },
  { value: 'tiktok', label: 'TikTok', Icon: TikTokIcon },
];
const STEPS = [
  { key: 'strategist', label: 'Strategist', desc: 'definisce angolo e messaggio', Icon: Lightbulb },
  { key: 'copywriter', label: 'Copywriter', desc: 'scrive la caption', Icon: PenLine },
  { key: 'reviewer', label: 'Reviewer', desc: 'controlla qualità e conformità', Icon: ShieldCheck },
];

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AIPostGeneratorDialog = ({ open, onOpenChange, onSaved }) => {
  const { API, getAuthHeaders } = useAuth();
  const [phase, setPhase] = useState('form');
  const [briefText, setBriefText] = useState('');
  const [goal, setGoal] = useState('farsi_conoscere');
  const [postFormat, setPostFormat] = useState('post');
  const [platforms, setPlatforms] = useState(['instagram']);
  const [media, setMedia] = useState([]);
  const [consent, setConsent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [caption, setCaption] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [feedback, setFeedback] = useState('');
  const [saving, setSaving] = useState(false);
  const [designedUrl, setDesignedUrl] = useState(null);
  const [showDesigner, setShowDesigner] = useState(false);
  const [showReel, setShowReel] = useState(false);
  const [reelUrl, setReelUrl] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const reset = () => {
    clearInterval(pollRef.current);
    setPhase('form'); setBriefText(''); setGoal('farsi_conoscere'); setPostFormat('post');
    setPlatforms(['instagram']); setMedia([]); setConsent(false); setJob(null); setCaption(''); setScheduledAt(''); setFeedback(''); setDesignedUrl(null); setShowDesigner(false); setShowReel(false); setReelUrl(null);
  };

  const handleClose = (o) => { if (!o) reset(); onOpenChange(o); };

  const togglePlatform = (p) => setPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/upload/media`, fd, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } });
        setMedia(prev => [...prev, { id: res.data.id, url: res.data.url, media_type: res.data.media_type, thumbnail_url: res.data.thumbnail_url }]);
      } catch (err) {
        toast.error(`Errore caricamento ${file.name}: ${err.response?.data?.detail || err.message}`);
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const removeMedia = (idx) => setMedia(prev => prev.filter((_, i) => i !== idx));

  const startPolling = (jobId) => {
    clearInterval(pollRef.current);
    const startedAt = Date.now();
    let failures = 0;
    pollRef.current = setInterval(async () => {
      if (Date.now() - startedAt > 4 * 60 * 1000) {
        clearInterval(pollRef.current);
        toast.error('La generazione sta impiegando troppo tempo. Riprova.');
        setPhase('form');
        return;
      }
      try {
        const res = await axios.get(`${API}/ai-social/jobs/${jobId}`, getAuthHeaders());
        setJob(res.data);
        if (res.data.status === 'done') {
          clearInterval(pollRef.current);
          const r = res.data.result;
          setCaption(`${r.caption}\n\n${(r.hashtags || []).join(' ')}`.trim());
          setScheduledAt(toLocalInput(r.suggested_time));
          setPhase('result');
        } else if (res.data.status === 'error') {
          clearInterval(pollRef.current);
          toast.error(res.data.error || 'Generazione fallita');
          setPhase('form');
        }
      } catch (err) {
        failures += 1;
        if (failures >= 4) {
          clearInterval(pollRef.current);
          toast.error('Errore nel recupero della generazione: il server non risponde, riprova tra poco.');
          setPhase('form');
        }
      }
    }, 2000);
  };

  const generate = async (extra = {}) => {
    if (!briefText.trim()) { toast.error('Descrivi cosa vuoi comunicare'); return; }
    if (!platforms.length) { toast.error('Seleziona almeno una piattaforma'); return; }
    if (media.length && !consent) { toast.error('Conferma di avere il consenso a pubblicare le foto'); return; }
    setPhase('running');
    setJob(null);
    try {
      const res = await axios.post(`${API}/ai-social/generate`, {
        brief_text: briefText, media_urls: media.map(m => m.url), platforms, goal, post_format: postFormat, ...extra,
      }, getAuthHeaders());
      startPolling(res.data.job_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore avvio generazione');
      setPhase('form');
    }
  };

  const regenerate = () => {
    const prev = caption;
    generate({ feedback: feedback || 'Proponi una variante diversa', previous_caption: prev });
    setFeedback('');
  };

  const saveDraft = async (withSchedule) => {
    setSaving(true);
    try {
      const scheduled = withSchedule && scheduledAt ? new Date(scheduledAt).toISOString() : null;
      await axios.post(`${API}/ai-social/jobs/${job.id}/save`, {
        content: caption, platforms, scheduled_time: scheduled, post_status: scheduled ? 'scheduled' : 'draft',
        post_format: reelUrl ? 'reel' : postFormat,
        media_urls: reelUrl ? [reelUrl] : designedUrl ? [designedUrl, ...media.filter(m => m.url !== firstImage?.url).map(m => m.url)] : undefined,
      }, getAuthHeaders());
      toast.success(scheduled ? 'Post programmato nel calendario' : 'Bozza salvata. La trovi tra le Bozze.');
      onSaved?.();
      handleClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const firstImage = media.find(m => m.media_type !== 'video');
  const imageUrls = media.filter(m => m.media_type !== 'video').map(m => m.url);
  const review = job?.result?.review;
  const scoreColor = review ? (review.score >= 80 ? 'text-emerald-600' : review.score >= 60 ? 'text-amber-600' : 'text-red-600') : '';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-generator-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> Social Media Manager AI
          </DialogTitle>
          <DialogDescription>
            {phase === 'form' && 'Descrivi in poche parole cosa vuoi comunicare: gli agenti AI faranno il resto.'}
            {phase === 'running' && 'Gli agenti stanno lavorando al tuo post...'}
            {phase === 'result' && 'Rivedi, modifica se serve e salva come bozza nel calendario.'}
          </DialogDescription>
        </DialogHeader>

        {phase === 'form' && (
          <div className="space-y-5 mt-2">
            <div className="space-y-2">
              <Label>Cosa vuoi comunicare? *</Label>
              <Textarea data-testid="ai-brief-input" rows={3} value={briefText} onChange={e => setBriefText(e.target.value)}
                placeholder="es. Promozione balayage -20% per tutto giugno · Nuovo trattamento viso all'acido ialuronico · Auguri di buona estate alle nostre clienti" />
            </div>

            <div className="space-y-2">
              <Label>Foto o video (opzionale)</Label>
              <div className="flex flex-wrap gap-2">
                {media.map((m, idx) => (
                  <div key={m.id} className="relative w-20 h-20 rounded-lg overflow-hidden border bg-muted">
                    {m.media_type === 'video'
                      ? (m.thumbnail_url ? <img src={`${API.replace('/api', '')}${m.thumbnail_url}`} alt="" className="w-full h-full object-cover" /> : <Film className="w-6 h-6 m-auto mt-7 text-muted-foreground" />)
                      : <img src={`${API.replace('/api', '')}${m.url}`} alt="" className="w-full h-full object-cover" />}
                    <button type="button" onClick={() => removeMedia(idx)} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5" data-testid={`ai-media-remove-${idx}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 text-muted-foreground" data-testid="ai-media-upload-label">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  <span className="text-[10px] mt-1">Carica</span>
                  <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleUpload} data-testid="ai-media-upload-input" />
                </label>
              </div>
              {media.length > 0 && (
                <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer pt-1">
                  <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5" data-testid="ai-consent-checkbox" />
                  Confermo di avere il consenso delle persone ritratte a pubblicare queste immagini.
                </label>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Obiettivo</Label>
                <div className="flex flex-wrap gap-2">
                  {GOALS.map(g => (
                    <Badge key={g.value} variant={goal === g.value ? 'default' : 'outline'} className="cursor-pointer py-1.5 px-3" onClick={() => setGoal(g.value)} data-testid={`ai-goal-${g.value}`}>
                      {g.label}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Formato</Label>
                <div className="flex gap-2">
                  {FORMATS.map(({ value, label, Icon }) => (
                    <Badge key={value} variant={postFormat === value ? 'default' : 'outline'} className="cursor-pointer py-1.5 px-3 gap-1" onClick={() => setPostFormat(value)} data-testid={`ai-format-${value}`}>
                      <Icon className="w-3.5 h-3.5" /> {label}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Canali</Label>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map(({ value, label, Icon }) => (
                  <Badge key={value} variant={platforms.includes(value) ? 'default' : 'outline'} className="cursor-pointer py-1.5 px-3 gap-1.5" onClick={() => togglePlatform(value)} data-testid={`ai-platform-${value}`}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={() => generate()} disabled={uploading} data-testid="ai-generate-btn">
                <Sparkles className="w-4 h-4 mr-2" /> Genera con AI
              </Button>
            </div>
          </div>
        )}

        {phase === 'running' && (
          <div className="py-8 space-y-4" data-testid="ai-running-state">
            {STEPS.map(({ key, label, desc, Icon }) => {
              const state = job?.steps?.[key] || 'pending';
              return (
                <div key={key} className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${state === 'running' ? 'border-primary bg-primary/5' : state === 'done' ? 'border-emerald-200 bg-emerald-50/50' : 'opacity-50'}`} data-testid={`ai-step-${key}`}>
                  <div className="w-9 h-9 rounded-full bg-background border flex items-center justify-center">
                    {state === 'done' ? <Check className="w-4 h-4 text-emerald-600" /> : state === 'running' ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : <Icon className="w-4 h-4 text-muted-foreground" />}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{label}</p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {phase === 'result' && job?.result && (
          <div className="space-y-5 mt-2" data-testid="ai-result-state">
            <div className="rounded-lg border p-3 bg-muted/30 text-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Strategia</p>
              <p data-testid="ai-strategy-angle">{job.result.strategy?.angle}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Caption</Label>
                <span className="text-xs text-muted-foreground">{caption.length} caratteri</span>
              </div>
              <Textarea data-testid="ai-caption-textarea" rows={10} value={caption} onChange={e => setCaption(e.target.value)} className="font-normal" />
              {postFormat === 'story' && job.result.caption_story && (
                <p className="text-xs text-muted-foreground">Versione breve per story: <span className="italic">{job.result.caption_story}</span></p>
              )}
            </div>

            {imageUrls.length >= 2 && (
              <div className="space-y-2" data-testid="ai-reel-section">
                <div className="flex items-center gap-3">
                  {reelUrl ? <video src={`${API.replace('/api', '')}${reelUrl}`} muted className="w-12 h-16 rounded-md object-cover border bg-black" data-testid="ai-result-reel-preview" /> : <Film className="w-8 h-8 text-muted-foreground" />}
                  <div className="flex-1 text-xs text-muted-foreground">{reelUrl ? 'Reel creato: verrà pubblicato come video.' : `Hai ${imageUrls.length} foto: vuoi trasformarle in un Reel con musica e titolo?`}</div>
                  {!showReel && !reelUrl && <Button size="sm" variant="outline" onClick={() => setShowReel(true)} data-testid="ai-reel-open-btn"><Film className="w-3.5 h-3.5 mr-1" /> Crea Reel</Button>}
                  {reelUrl && <Button size="sm" variant="ghost" onClick={() => setReelUrl(null)} data-testid="ai-reel-reset-btn">Rimuovi</Button>}
                </div>
                {showReel && (
                  <ReelBuilderPanel mediaUrls={imageUrls} defaultHeadline={job.result.headline || ''} defaultSubline={job.result.cta || ''}
                    onApply={(url) => { setReelUrl(url); setPostFormat('reel'); setShowReel(false); toast.success('Reel pronto: il post sarà pubblicato come Reel'); }} onCancel={() => setShowReel(false)} />
                )}
              </div>
            )}

            {firstImage && !reelUrl && (
              <div className="space-y-2" data-testid="ai-designer-section">
                <div className="flex items-center gap-3">
                  <img src={`${API.replace('/api', '')}${designedUrl || firstImage.url}`} alt="" className="w-16 h-16 rounded-md object-cover border" data-testid="ai-result-media-preview" />
                  <div className="flex-1 text-xs text-muted-foreground">{designedUrl ? 'Grafica brandizzata applicata.' : 'Vuoi aggiungere logo, colore e titolo sulla foto?'}</div>
                  {!showDesigner && (
                    <Button size="sm" variant="outline" onClick={() => setShowDesigner(true)} data-testid="ai-designer-open-btn"><Palette className="w-3.5 h-3.5 mr-1" /> Designer</Button>
                  )}
                  {designedUrl && <Button size="sm" variant="ghost" onClick={() => setDesignedUrl(null)} data-testid="ai-designer-reset-btn">Ripristina</Button>}
                </div>
                {showDesigner && (
                  <DesignerPanel mediaUrl={firstImage.url} defaultHeadline={job.result.headline || ''} defaultSubline={job.result.cta || ''} defaultFormat={postFormat === 'story' ? 'story' : 'feed'}
                    onApply={(url) => { setDesignedUrl(url); setShowDesigner(false); toast.success('Grafica applicata al post'); }} onCancel={() => setShowDesigner(false)} />
                )}
              </div>
            )}

            {review && (
              <div className="rounded-lg border p-3 space-y-2" data-testid="ai-review-panel">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" />
                    <span className="text-sm font-medium">Controllo qualità</span>
                    {review.approved ? <Badge variant="outline" className="text-emerald-700 border-emerald-300">Approvato</Badge> : <Badge variant="outline" className="text-amber-700 border-amber-300">Da rivedere</Badge>}
                  </div>
                  <span className={`text-lg font-bold ${scoreColor}`} data-testid="ai-review-score">{review.score}/100</span>
                </div>
                {review.summary && <p className="text-sm text-muted-foreground">{review.summary}</p>}
                {review.issues?.length > 0 && (
                  <ul className="space-y-1">
                    {review.issues.map((i, idx) => (
                      <li key={idx} className="text-xs flex items-start gap-1.5">
                        <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${i.severity === 'high' ? 'text-red-500' : i.severity === 'medium' ? 'text-amber-500' : 'text-muted-foreground'}`} />
                        {i.text}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Programma per</Label>
              <Input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} className="w-full sm:w-64" data-testid="ai-schedule-input" />
              <p className="text-xs text-muted-foreground">Suggerito in base ai giorni e all'orario preferiti nel Brand Brief.</p>
            </div>

            <div className="border-t pt-4 space-y-2">
              <Label className="text-xs text-muted-foreground">Non ti convince? Dai un feedback e rigenera</Label>
              <div className="flex gap-2">
                <Input value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="es. più corto, meno emoji, più elegante" data-testid="ai-feedback-input" />
                <Button variant="outline" onClick={regenerate} data-testid="ai-regenerate-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Rigenera
                </Button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => saveDraft(false)} disabled={saving} data-testid="ai-save-draft-btn">
                <Save className="w-4 h-4 mr-2" /> Salva come bozza
              </Button>
              <Button onClick={() => saveDraft(true)} disabled={saving || !scheduledAt} data-testid="ai-save-scheduled-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Programma nel calendario
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AIPostGeneratorDialog;
